import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Masonry from 'react-masonry-css';
import ArtworkModal from '@/components/ArtworkModal';
import ShareButtons from '@/components/ShareButtons';
import WishlistButton from '@/components/WishlistButton';
import ProgressiveImage from '@/components/ProgressiveImage';
import { Search, ShoppingCart, Printer, Download, Loader2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/RegionContext';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import { trackArtworkView } from '@/components/PageTracker';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

const Gallery = () => {
  const [artworks, setArtworks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedArtType, setSelectedArtType] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [seoKeywords, setSeoKeywords] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Redirect old ?artwork=id URLs to the new /artwork/:id page
  useEffect(() => {
    const artworkId = searchParams.get('artwork');
    if (artworkId) {
      navigate(`/artwork/${artworkId}`, { replace: true });
      return;
    }
    // Pre-select art_type filter from URL (e.g. /gallery?art_type=Traditional)
    const urlArtType = searchParams.get('art_type');
    if (urlArtType && ['Traditional', 'Digital'].includes(urlArtType)) {
      setSelectedArtType(urlArtType);
    }
  }, [searchParams, navigate]);

  const handleAddToCart = (e, artwork) => {
    e.stopPropagation();
    addToCart({
      id: artwork.id,
      title: artwork.title,
      price: artwork.price || (artwork.art_type === 'Digital' ? 5000 : 10000),
      image_url: artwork.image_url,
      art_type: artwork.art_type,
      category: artwork.category,
    });
    toast.success(`"${artwork.title}" added to cart`);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchArtworks = useCallback(async (pageNum, append = false) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);

      const params = new URLSearchParams({ page: pageNum, limit: PAGE_SIZE });
      if (selectedCategory !== 'All') params.set('category', selectedCategory);
      if (selectedArtType !== 'All') params.set('art_type', selectedArtType);
      if (debouncedSearch) params.set('search', debouncedSearch);

      const res = await axios.get(`${API}/artworks?${params}`);
      const data = res.data;

      if (data.items) {
        setArtworks(prev => append ? [...prev, ...data.items] : data.items);
        setTotalPages(data.pages);
        setTotal(data.total);
      } else {
        // Fallback: non-paginated response
        setArtworks(Array.isArray(data) ? data : []);
        setTotalPages(1);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedCategory, selectedArtType, debouncedSearch]);

  // Fetch initial + categories
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [categoriesRes, contentRes, seoRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/page-content/gallery`).catch(() => ({ data: null })),
          axios.get(`${API}/seo/keywords`).catch(() => ({ data: null }))
        ]);
        setCategories(['All', ...categoriesRes.data]);
        setPageContent(contentRes.data);
        if (seoRes.data?.keywords) setSeoKeywords(seoRes.data.keywords);
      } catch (error) {
        console.error(error);
      }
    };
    fetchMeta();
  }, []);

  // Reset to page 1 on filter/search change
  useEffect(() => {
    setPage(1);
    fetchArtworks(1, false);
  }, [fetchArtworks]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchArtworks(nextPage, true);
  };

  const breakpointColumns = { default: 3, 1024: 2, 640: 1 };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="gallery-page">
      <SEO
        title="Gallery - Gangadhar Som Paintings | Buy Original Art & Prints"
        description="Browse and buy original paintings and art prints by Gangadhar Som. Featuring Radha Krishna, devotional art, Indian mythology, oil paintings, and more. Worldwide shipping available."
        keywords={seoKeywords || "Gangadhar Som paintings, Radha Krishna art, Indian mythology paintings, buy paintings online, devotional art, Krishna paintings, oil paintings for sale"}
        url={`${window.location.origin}/gallery`}
      />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 sm:mb-16">
          <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">{pageContent?.page_label || 'Portfolio'}</span>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light tracking-tight mt-3" data-testid="gallery-page-title">
            {pageContent?.page_title || 'Gallery'}
          </h1>
          {total > 0 && (
            <p className="font-sans text-sm text-muted-foreground mt-2">{total} artwork{total !== 1 ? 's' : ''}</p>
          )}
        </motion.div>

        {/* Filters */}
        <div className="mb-8 sm:mb-12 space-y-5">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              type="text" placeholder="Search artworks..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
              className="w-full pl-11 pr-4 py-2.5 bg-card border border-border rounded-full font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent transition-shadow"
            />
          </div>
          <div>
            <p className="font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs font-medium text-muted-foreground mb-2">Art Type</p>
            <div className="flex flex-wrap gap-2">
              {['All', 'Traditional', 'Digital'].map((type) => (
                <button key={type} onClick={() => setSelectedArtType(type)} data-testid={`art-type-filter-${type.toLowerCase()}`}
                  className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-sans uppercase tracking-[0.15em] text-[10px] sm:text-xs font-medium transition-all ${
                    selectedArtType === type ? 'bg-accent text-white shadow-md' : 'bg-card border border-border hover:border-accent'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs font-medium text-muted-foreground mb-2">Category</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button key={category} onClick={() => setSelectedCategory(category)}
                  data-testid={`category-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-sans uppercase tracking-[0.15em] text-[10px] sm:text-xs font-medium transition-all ${
                    selectedCategory === category ? 'bg-accent text-white shadow-md' : 'bg-card border border-border hover:border-accent'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="loading-indicator">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton aspect-[4/5] rounded-lg" />
            ))}
          </div>
        ) : artworks.length === 0 ? (
          <div className="text-center py-20" data-testid="no-artworks-message">
            <p className="font-sans text-muted-foreground">No artworks found.</p>
          </div>
        ) : (
          <>
            <Masonry breakpointCols={breakpointColumns} className="masonry-grid" columnClassName="masonry-grid_column">
              {artworks.map((artwork, index) => (
                <motion.div
                  key={artwork.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.04, 0.5) }}
                  onClick={() => { navigate(`/artwork/${artwork.id}`); }}
                  className="group cursor-pointer image-hover"
                  data-testid={`artwork-card-${index}`}
                >
                  <div className="artwork-frame relative overflow-hidden protected-image select-none">
                    <ProgressiveImage
                      src={artwork.thumb_url || artwork.image_url}
                      placeholder={artwork.placeholder}
                      alt={`${artwork.title} — ${artwork.medium || 'painting'} by Gangadhar Som${artwork.year ? ` (${artwork.year})` : ''}`}
                      className="w-full h-auto pointer-events-none select-none"
                      style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
                    />
                    <div className="absolute inset-0 z-[1]" />
                    <div className="absolute inset-0 z-[2] pointer-events-none flex items-center justify-center opacity-[0.08]"
                      style={{ transform: 'rotate(-25deg)' }}>
                      <span className="text-white font-serif text-3xl sm:text-4xl md:text-5xl whitespace-nowrap tracking-widest select-none">
                        gangadharsom.art
                      </span>
                    </div>
                    <div className="image-overlay z-[3]">
                      <h3 className="font-serif text-base sm:text-xl md:text-2xl font-light text-white mb-0.5">
                        {artwork.title}
                      </h3>
                      <div className="flex items-center justify-between w-full">
                        <div>
                          {artwork.year && <p className="font-sans text-xs text-white/80">{artwork.year}</p>}
                          {artwork.for_sale === false ? (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-red-500/90 text-white font-sans uppercase tracking-wider text-[10px] rounded-full" data-testid={`artwork-sold-${index}`}>Sold</span>
                          ) : (
                            <p className="font-serif text-sm sm:text-lg text-white mt-0.5" data-testid={`artwork-price-${index}`}>
                              {formatPrice(artwork.price || (artwork.art_type === 'Digital' ? 5000 : 10000))}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <WishlistButton artworkId={artwork.id} variant="compact" />
                          <ShareButtons artwork={artwork} variant="compact" />
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/print/${artwork.id}`); }}
                            data-testid={`order-print-${index}`}
                            className="p-2 sm:p-2.5 bg-white/20 hover:bg-white/40 text-white rounded-full transition-transform hover:scale-110 active:scale-95"
                            title="Order Print"
                          >
                            <Printer size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedArtwork(artwork); setIsModalOpen(true); trackArtworkView(artwork.id, artwork.title); }}
                            data-testid={`digital-download-${index}`}
                            className="p-2 sm:p-2.5 bg-violet-500/80 hover:bg-violet-500 text-white rounded-full transition-transform hover:scale-110 active:scale-95"
                            title={`Buy Digital Download — ${formatPrice(499)}`}
                          >
                            <Download size={16} />
                          </button>
                          {artwork.for_sale !== false && (
                            <button
                              onClick={(e) => handleAddToCart(e, artwork)}
                              data-testid={`add-to-cart-${index}`}
                              className="p-2 sm:p-2.5 bg-accent hover:bg-accent/80 text-white rounded-full transition-transform hover:scale-110 active:scale-95"
                              title="Buy Original"
                            >
                              <ShoppingCart size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </Masonry>

            {/* Load More */}
            {page < totalPages && (
              <div className="text-center mt-12" data-testid="load-more-container">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  data-testid="load-more-btn"
                  className="px-10 py-3 rounded-full font-sans uppercase tracking-[0.15em] text-xs"
                >
                  {loadingMore ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Loading...</>
                  ) : (
                    `Load More (${artworks.length} of ${total})`
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <ArtworkModal artwork={selectedArtwork} isOpen={isModalOpen} onClose={() => {
        setIsModalOpen(false);
        if (searchParams.get('artwork')) {
          setSearchParams({}, { replace: true });
        }
      }} />
    </div>
  );
};

export default Gallery;
